/**
 * This program is free software; you can redistribute it and/or
 * modify it under the terms of the GNU General Public License
 * as published by the Free Software Foundation; under version 2
 * of the License (non-upgradable).
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program; if not, write to the Free Software
 * Foundation, Inc., 51 Franklin Street, Fifth Floor, Boston, MA  02110-1301, USA.
 *
 * Copyright (c) 2022 (original work) Open Assessment Technologies SA ;
 */
import { getActualKey } from '../input/keys.js';

/**
 * Svelte Action to focus previous and next elements inside a container on arrow keys.
 * - `tabindex` shoud be managed from outside: there should be only one tabstop element.
 * - for svg node, set `tabindex="-1"` on it: otherwise it will become focusable once `focusin` handler is bound.
 *
 * @example Usage 1: loop based on key order; remember last focused element; control tabindex on container
 * <div tabindex={hasFocus ? "-1" : "0"} on:focus={forwardFocusToChoice(lastFocusedKey)}>
 *   <svg>
 *     <g class="list" tabindex="-1"
 *       use:arrowKeysFocusLoop={{ keysInTabOrder, lastFocusedKey, focusByKey, isRTL}}
 *       on:setLastFocusedKey={lastFocusedKey = e.detail}>
 *       {#each choices as choice}
 *         <g class="choice" />
 *       {/each}
 *     </g>
 *   <svg>
 * </div>
 *
 * @example Usage 2: loop based on element order; do not remember last focused element; control tabindex on choice
 * <ul
 *   use:arrowKeysFocusLoop={{ getFocusableElements, isRTL}}
 *   on:setHasFocus={() => {hasFocus = e.detail}}>
 *   {#each choices as choice, index}
 *     <li tabindex={!hasFocus ? "0" : "-1"} />
 *   {/each}
 * </ul>
 *
 * @fires setHasFocus
 * @fires setLastFocusedKey
 *
 * @param {HTMLElement} node - container node inside which the focusables reside
 * @param {Object} options
 * @param {String[]} [options.keysInTabOrder] - set unless `getFocusableElements` - keys of focusable elements in desired order
 * @param {String} [options.lastFocusedKey] - set unless `getFocusableElements` - key of most recently focused element
 * @param {Function} [options.focusByKey] - {(newFocusedChoiceKey: String) => void *} - set unless `getFocusableElements` - focus focusable element with this key
 * @param {Function} [options.getFocusableElements] - {() => NodeList|Array<HTMLElement>} - set unless `keysInTabOrder/lastFocusedKey/focusByKey`
 * @param {Boolean} [options.isRTL=false]
 * @returns {Object} - node's lifecycle hooks
 */
export default function arrowKeysFocusLoop(
    node,
    { keysInTabOrder, lastFocusedKey, focusByKey, getFocusableElements, isRTL = false }
) {
    let hasFocus = false;

    /**
     * Focus a sibling choice in the tabbing order
     * Supports looping from last to first
     * @param {Number} [offset=1] use 1 for next, -1 for previous
     * @fires setLastFocusedKey - fired unless `getFocusableElements`
     */
    function focusSibling(offset = 1) {
        if (getFocusableElements) {
            const focusableElements = Array.from(getFocusableElements());
            const index = focusableElements.findIndex(el => el.contains(document.activeElement));
            const newIndex = (focusableElements.length + index + offset) % focusableElements.length;
            focusableElements[newIndex].focus();
        } else {
            if (lastFocusedKey || lastFocusedKey === 0) {
                const index = keysInTabOrder.indexOf(lastFocusedKey);
                const newIndex = (keysInTabOrder.length + index + offset) % keysInTabOrder.length;
                const newFocusedChoiceKey = keysInTabOrder[newIndex];
                if (focusByKey(newFocusedChoiceKey)) {
                    lastFocusedKey = newFocusedChoiceKey;
                    node.dispatchEvent(
                        new CustomEvent('setLastFocusedKey', {
                            detail: newFocusedChoiceKey,
                            bubbles: false
                        })
                    );
                }
            }
        }
    }

    /**
     * Handle key navigation (only arrow keys move between available choices)
     * @param {KeyboardEvent} event
     */
    function handleKeyDown(event) {
        const pressedKey = getActualKey(event);
        switch (pressedKey) {
            case 'down':
            case !isRTL && 'right':
            case isRTL && 'left':
                if (!getFocusableElements || Array.from(getFocusableElements()).includes(event.target)) {
                    event.preventDefault();
                    focusSibling(1);
                }
                break;

            case 'up':
            case !isRTL && 'left':
            case isRTL && 'right':
                if (!getFocusableElements || Array.from(getFocusableElements()).includes(event.target)) {
                    event.preventDefault();
                    focusSibling(-1);
                }
                break;
        }
    }

    /**
     * Handle focusin on window
     * @param {Event} event
     * @fires setHasFocus
     */
    function handleWindowFocusin(event) {
        if (hasFocus) {
            const focusIsOutside = node === event.target || !node.contains(event.target);
            // otherwise, if another behavior is preferred for the case
            //   where there are other focusable elements inside `node` besides `getFocusableElements()`:
            // const focusIsOutside = !getFocusableElements
            //     ? node === event.target || !node.contains(event.target)
            //     : !Array.from(getFocusableElements()).includes(event.target);
            if (focusIsOutside) {
                window.removeEventListener('focusin', handleWindowFocusin);
                hasFocus = false;

                node.dispatchEvent(
                    new CustomEvent('setHasFocus', {
                        detail: false,
                        bubbles: false
                    })
                );
            }
        }
    }

    /**
     * Handle focusin on container node
     * @param {Event} event
     * @fires setHasFocus
     */
    function handleFocusin(event) {
        if (!hasFocus) {
            const focusIsInside = !getFocusableElements || Array.from(getFocusableElements()).includes(event.target);
            if (focusIsInside) {
                window.addEventListener('focusin', handleWindowFocusin);
                hasFocus = true;

                node.dispatchEvent(
                    new CustomEvent('setHasFocus', {
                        detail: true,
                        bubbles: false
                    })
                );
            }
        }
    }

    /**
     * Handle focusout on container node
     * @fires setHasFocus
     */
    function handleFocusout() {
        setTimeout(() => {
            if (hasFocus) {
                const focusIsOutside = node === document.activeElement || !node.contains(document.activeElement);
                if (focusIsOutside) {
                    window.removeEventListener('focusin', handleWindowFocusin);
                    hasFocus = false;

                    node.dispatchEvent(
                        new CustomEvent('setHasFocus', {
                            detail: false,
                            bubbles: false
                        })
                    );
                }
            }
        }, 200);
    }

    // Initialise the listener
    node.addEventListener('keydown', handleKeyDown);
    node.addEventListener('focusin', handleFocusin);
    node.addEventListener('focusout', handleFocusout);

    return {
        /**
         * Hook on component lifecyle: remove listener
         */
        destroy() {
            node.removeEventListener('keydown', handleKeyDown);
            node.removeEventListener('focusin', handleFocusin);
            window.removeEventListener('focusin', handleWindowFocusin);
        },

        /**
         * Hook to update action params
         * @param {Object} newOptions
         */
        update(newOptions) {
            ({ keysInTabOrder, lastFocusedKey, focusByKey, isRTL } = newOptions);
        }
    };
}
